import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi, isConflictError } from '../../api/adsMemberApi';
import './ads.css';

export default function CreativeDetailPage() {
  const { creativeId } = useParams();
  const { t } = useTravelTranslation();

  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    setConflict(false);

    adsMemberApi.creatives
      .get(creativeId)
      .then((data) => {
        setCreative(data);
        setHeadline(data.headline || '');
        setSummary(data.summary || '');
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || '載入廣告素材詳情失敗');
        setLoading(false);
      });
  }, [creativeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!creative) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await adsMemberApi.creatives.update(creativeId, {
        headline: headline.trim(),
        summary: summary.trim(),
        expectedRowVersion: creative.rowVersion,
      });
      setCreative(res);
      setIsEditing(false);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || '更新素材失敗');
      }
      setActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!creative) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await adsMemberApi.creatives.submit(creativeId, {
        expectedRowVersion: creative.rowVersion,
      });
      setCreative(res);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || '送出審核失敗');
      }
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!creative) return;
    setActionLoading(true);
    setError('');
    setConflict(false);

    try {
      const res = await adsMemberApi.creatives.cancel(creativeId, {
        expectedRowVersion: creative.rowVersion,
      });
      setCreative(res);
      setActionLoading(false);
    } catch (err) {
      if (isConflictError(err)) {
        setConflict(true);
      } else {
        setError(err?.message || '取消素材失敗');
      }
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="ads-page" data-testid="creative-detail-loading">
        <div className="ads-loading">{t('travel.ads.common.loading')}</div>
      </main>
    );
  }

  if (error && !creative) {
    return (
      <main className="ads-page" data-testid="creative-detail-error">
        <div className="ads-alert ads-alert--error">{error}</div>
        <Link to="/me/ads" className="ads-btn ads-btn--secondary">
          {t('travel.ads.common.backToAds')}
        </Link>
      </main>
    );
  }

  const status = creative?.status || 'DRAFT';
  const isDraft = status === 'DRAFT';
  const isSubmitted = status === 'SUBMITTED';
  const isRejected = status === 'REJECTED';

  return (
    <main className="ads-page" data-testid="creative-detail-page">
      <header className="ads-header">
        <div>
          <h1 className="ads-title">{creative?.headline || `素材 #${creative?.creativeId}`}</h1>
          <span className={`ads-badge ads-badge--${status}`} data-testid="creative-status-badge">
            {t(`travel.ads.status.${status}`) || status}
          </span>
        </div>
        <div className="ads-header__actions">
          <Link to="/me/ads" className="ads-btn ads-btn--secondary">
            {t('travel.ads.common.backToAds')}
          </Link>
        </div>
      </header>

      {conflict ? (
        <div className="ads-alert ads-alert--conflict" data-testid="creative-conflict-alert">
          <span>{t('travel.ads.conflictMessage')}</span>
          <button
            type="button"
            className="ads-btn ads-btn--secondary"
            onClick={load}
            data-testid="btn-creative-refresh"
          >
            {t('travel.ads.common.refresh')}
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="ads-alert ads-alert--error" data-testid="creative-action-error">
          {error}
        </div>
      ) : null}

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">基本資訊</h4>
        <div className="ads-grid-2">
          <div className="ads-metric">
            <div className="ads-metric__label">素材 ID</div>
            <div className="ads-metric__value" data-testid="creative-id">
              {creative?.creativeId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">推廣申請 ID</div>
            <div className="ads-metric__value" data-testid="creative-promotion-id">
              {creative?.promotionApplicationId}
            </div>
          </div>
          <div className="ads-metric">
            <div className="ads-metric__label">版本 (rowVersion)</div>
            <div className="ads-metric__value" data-testid="creative-row-version">
              {creative?.rowVersion}
            </div>
          </div>
        </div>

        {isRejected && creative?.rejectionReason ? (
          <div className="ads-card__rejection" data-testid="creative-rejection-reason">
            <strong>{t('travel.ads.creatives.rejectionReason')}：</strong>
            {creative.rejectionReason}
          </div>
        ) : null}
      </section>

      {isDraft && isEditing ? (
        <section className="ads-detail-section" data-testid="creative-edit-section">
          <h4 className="ads-detail-section__title">編輯素材</h4>
          <form className="ads-form" onSubmit={handleUpdate}>
            <div className="ads-form-group">
              <label className="ads-form-label" htmlFor="edit-headline">
                {t('travel.ads.creatives.headline')}
              </label>
              <input
                id="edit-headline"
                type="text"
                maxLength={100}
                className="ads-form-input"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                disabled={actionLoading}
                data-testid="input-edit-headline"
              />
            </div>
            <div className="ads-form-group">
              <label className="ads-form-label" htmlFor="edit-summary">
                {t('travel.ads.creatives.summary')}
              </label>
              <textarea
                id="edit-summary"
                maxLength={500}
                className="ads-form-textarea"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={actionLoading}
                data-testid="input-edit-summary"
              />
            </div>
            <div className="ads-header__actions">
              <button
                type="submit"
                className="ads-btn ads-btn--primary"
                disabled={actionLoading}
                data-testid="btn-save-creative-edit"
              >
                {t('travel.ads.common.save')}
              </button>
              <button
                type="button"
                className="ads-btn ads-btn--secondary"
                onClick={() => setIsEditing(false)}
                disabled={actionLoading}
              >
                {t('travel.ads.promotions.cancel')}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="ads-detail-section" data-testid="creative-content-section">
          <h4 className="ads-detail-section__title">素材內容</h4>
          <p><strong>標題：</strong><span data-testid="creative-headline">{creative?.headline}</span></p>
          <p><strong>摘要：</strong><span data-testid="creative-summary">{creative?.summary}</span></p>
        </section>
      )}

      <section className="ads-detail-section">
        <h4 className="ads-detail-section__title">生命週期操作</h4>
        <div className="ads-header__actions">
          {isDraft && !isEditing ? (
            <button
              type="button"
              className="ads-btn ads-btn--secondary"
              onClick={() => setIsEditing(true)}
              disabled={actionLoading}
              data-testid="btn-creative-edit-toggle"
            >
              {t('travel.ads.creatives.edit')}
            </button>
          ) : null}

          {isDraft ? (
            <button
              type="button"
              className="ads-btn ads-btn--primary"
              onClick={handleSubmit}
              disabled={actionLoading}
              data-testid="btn-creative-submit"
            >
              {t('travel.ads.creatives.submit')}
            </button>
          ) : null}

          {isDraft || isSubmitted ? (
            <button
              type="button"
              className="ads-btn ads-btn--danger"
              onClick={handleCancel}
              disabled={actionLoading}
              data-testid="btn-creative-cancel"
            >
              {t('travel.ads.creatives.cancel')}
            </button>
          ) : null}
        </div>
      </section>
    </main>
  );
}
